// JSON-LD for the marketing pages, /pricing and blog articles, rendered into the
// initial HTML by <JsonLd> so crawlers and AI answer engines read it without
// running scripts. The nodes reference each other by `@id`.

import type { Article } from "../types/article";
import { LOCALES } from "./i18n";
import { buildKlapOffersJsonLd } from "./publicPricing";
import { DEFAULT_OG_IMAGE_URL, SITE_URL } from "./seo";
import { KLAP_DESCRIPTION } from "./webConst";

const ORGANIZATION_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;
const APP_ID = `${SITE_URL}/#app`;
const LOGO_URL = `${SITE_URL}/svg/logomark.svg`;

// Profiles checked as Klap's own on 2026-11-04. Add Wikidata when the item
// exists. Don't add a TikTok, Instagram or LinkedIn profile without checking
// it: the obvious handles belong to other people.
const ORGANIZATION_SAME_AS = [
  "https://x.com/klap_app",
  "https://www.youtube.com/@klapapp",
  "https://www.crunchbase.com/organization/klap-a485",
  "https://www.trustpilot.com/review/klap.app",
  "https://www.g2.com/products/klap/reviews",
  "https://www.producthunt.com/products/klap-2",
  "https://www.capterra.com/p/10014703/Klap/",
  "https://alternativeto.net/software/klap/about/",
];

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: "Klap",
    legalName: "ZIGG SAS",
    url: SITE_URL,
    logo: LOGO_URL,
    sameAs: ORGANIZATION_SAME_AS,
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "support@klap.app",
      url: `${SITE_URL}/support`,
    },
  };
}

// No SearchAction: the site has no search page.
export function webSiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    name: "Klap",
    url: SITE_URL,
    inLanguage: [...LOCALES],
    publisher: { "@id": ORGANIZATION_ID },
  };
}

export function softwareApplicationJsonLd(now: Date = new Date()) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": APP_ID,
    name: "Klap",
    url: SITE_URL,
    description: KLAP_DESCRIPTION,
    image: DEFAULT_OG_IMAGE_URL,
    applicationCategory: "MultimediaApplication",
    operatingSystem: "Web",
    publisher: { "@id": ORGANIZATION_ID },
    offers: buildKlapOffersJsonLd(now),
  };
}

export function faqPageJsonLd(faq: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  };
}

export function blogPostingJsonLd(article: Article, description: string) {
  const url = `${SITE_URL}/blog/${article.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    url,
    headline: article.title,
    description,
    image: [article.image?.url || DEFAULT_OG_IMAGE_URL],
    datePublished: article.publishedDate,
    dateModified: article.updatedAt || article.publishedDate,
    inLanguage: "en",
    ...(article.category && { articleSection: article.category.name }),
    author: article.author
      ? {
          "@type": "Person",
          name: article.author.name,
          ...(article.author.link && { url: article.author.link }),
        }
      : { "@id": ORGANIZATION_ID },
    publisher: {
      "@type": "Organization",
      "@id": ORGANIZATION_ID,
      name: "Klap",
      logo: { "@type": "ImageObject", url: LOGO_URL },
    },
  };
}
